import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/store/AuthContext';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { Vendor } from '@/types/dashboard/vendor';
import { VendorFormModal } from './VendorFormModal';
import { useDeleteVendor, useUpdateVendor, useVendors } from './useVendors';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

export function VendorsPage() {
  const { user } = useAuth();
  const merchantId = user?.scope.merchant_id ?? null;

  const { data: vendors = [], isLoading, isError, error, refetch } = useVendors();
  const updateVendor = useUpdateVendor();
  const deleteVendor = useDeleteVendor();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [toDelete, setToDelete] = useState<Vendor | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (v: Vendor) => {
    setEditing(v);
    setFormOpen(true);
  };

  const toggleActive = async (v: Vendor) => {
    try {
      await updateVendor.mutateAsync({ id: v.id, payload: { is_active: !v.is_active } });
      toast.success(v.is_active ? 'Vendor deactivated' : 'Vendor activated');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteVendor.mutateAsync(toDelete.id);
      toast.success('Vendor deleted');
      setToDelete(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h2 className="text-headline-lg text-on-surface mb-1">Vendors</h2>
          <p className="text-body-md text-on-surface-variant mb-0">
            Onboard and manage your print vendors.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary tap-target"
          onClick={openCreate}
          disabled={merchantId === null}
          title={merchantId === null ? 'Your account is not linked to a merchant' : undefined}
        >
          <i className="bi bi-plus-lg me-2" />
          Onboard vendor
        </button>
      </div>

      <div className="card shadow-kiosk-sm">
        <div className="card-body">
          {isLoading && (
            <div className="text-center py-5 text-on-surface-variant">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading vendors…
            </div>
          )}

          {isError && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
              <span className="text-body-sm">{getApiErrorMessage(error, 'Could not load vendors')}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && vendors.length === 0 && (
            <div className="text-center py-5">
              <span
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-surface-low text-primary mb-3"
                style={{ width: 56, height: 56 }}
              >
                <i className="bi bi-shop fs-4" />
              </span>
              <div className="text-headline-sm text-on-surface">No vendors yet</div>
              <p className="text-body-md text-on-surface-variant">Onboard your first vendor to get started.</p>
            </div>
          )}

          {vendors.length > 0 && (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="text-label-sm text-on-surface-variant">
                    <th className="bg-surface-low border-0 rounded-start-3">Vendor</th>
                    <th className="bg-surface-low border-0">Contact person</th>
                    <th className="bg-surface-low border-0">Phone</th>
                    <th className="bg-surface-low border-0">Status</th>
                    <th className="bg-surface-low border-0">Onboarded</th>
                    <th className="bg-surface-low border-0 rounded-end-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((v) => (
                    <tr key={v.id} className="text-body-sm text-on-surface">
                      <td>
                        <Link
                          to={`${ROUTES.admin.vendors}/${v.id}`}
                          className="fw-semibold text-on-surface text-decoration-none vendor-link"
                          title={`Open ${v.name}`}
                        >
                          {v.name}
                        </Link>
                        <div className="text-on-surface-variant">{v.email}</div>
                      </td>
                      <td>{v.contact_person}</td>
                      <td>{v.phone}</td>
                      <td>
                        <span
                          className={`badge rounded-pill ${
                            v.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'
                          }`}
                        >
                          {v.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-on-surface-variant">{formatDate(v.created_at)}</td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-link text-on-surface-variant p-1"
                          title={v.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleActive(v)}
                          disabled={updateVendor.isPending}
                        >
                          <i className={`bi ${v.is_active ? 'bi-toggle-on text-success' : 'bi-toggle-off'} fs-5`} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link text-on-surface-variant p-1"
                          title="Edit"
                          onClick={() => openEdit(v)}
                        >
                          <i className="bi bi-pencil fs-6" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link text-danger p-1"
                          title="Delete"
                          onClick={() => setToDelete(v)}
                        >
                          <i className="bi bi-trash fs-6" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {formOpen && (
        <VendorFormModal vendor={editing} merchantId={merchantId} onClose={() => setFormOpen(false)} />
      )}

      <Modal show={toDelete !== null} onHide={() => setToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-headline-sm">Delete vendor</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-body-md">
          Delete <strong>{toDelete?.name}</strong>? This removes the vendor record. Their login account is not
          removed by this action.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setToDelete(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleteVendor.isPending}>
            {deleteVendor.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}